import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

import { applyTheme, getTheme } from "../lib/theme";
import type { LandingSection } from "./LandingPage";
import { LandingMotion } from "./marketing/LandingMotion";
import { LandingCosts, LandingTail } from "./marketing/LandingTail";
import { LandingTop } from "./marketing/LandingTop";
import { MarketingFooter, MarketingHeader } from "./marketing/MarketingChrome";
import { PitchOpening } from "./marketing/PitchOpening";
import { ScrollRail } from "./marketing/ScrollRail";
import { useActiveSection } from "./marketing/useActiveSection";
import { useHeaderOffset } from "./marketing/useHeaderOffset";
import "./landing-art.css";
import "./marketing/roster-story.css";
import "./marketing/landing-palette.css";
import "./marketing/word-design.css";

// ─────────────────────────────────────────────────────────────────────
// The seven pitch sections, held as data: they feed the scroll rail, the
// per-section tint and the anchor ids in one place so the three cannot
// disagree. `tint` names a palette block in `landing-art.css`; `null` is
// the sage base. All seven sections sit on the sage base.
// ─────────────────────────────────────────────────────────────────────

export const PITCH_SECTIONS: ReadonlyArray<LandingSection> = [
  { id: "return", no: "", name: "Who we are", tint: null },
  { id: "built-so-far", no: "", name: "What we built so far", tint: null },
  { id: "shift-problem", no: "", name: "The problem for a shift team", tint: null },
  { id: "rostering-wrong", no: "", name: "Where rostering goes wrong", tint: null },
  { id: "what-it-costs", no: "", name: "What it costs", tint: null },
  { id: "beside-systems", no: "", name: "Tarii beside your systems", tint: null },
  { id: "next-step", no: "", name: "Next step", tint: null },
];

const PITCH_IDS = PITCH_SECTIONS.map((section) => section.id);

export function ProblemPage() {
  const { hash } = useLocation();
  useHeaderOffset();
  // The page starts at the pinned header. A hash link scrolls to its
  // section once the page is mounted.
  useLayoutEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "instant", block: "start" });
  }, [hash]);
  useEffect(() => {
    const previous = document.title;
    document.title = "The problem with rostering | Tarii";
    return () => {
      document.title = previous;
    };
  }, []);
  // Pin the public page to `sage-clinic` (founder direction
  // 2026-08-01). `applyTheme` writes ONLY the `<html data-theme>`
  // attribute — the visitor's stored preference in localStorage is
  // never touched, and `getTheme()` restores it on unmount so the
  // authenticated app they walk into looks exactly as they left it. A
  // layout effect (not `useEffect`) so the swap lands before paint and
  // a returning dark-theme visitor sees no flash.
  useLayoutEffect(() => {
    applyTheme("sage-clinic");
    return () => applyTheme(getTheme());
  }, []);

  // Round 8: the active section drives the scroll rail AND the
  // per-section pastel shift. The tint is scoped CSS-variable overrides
  // on THIS root (`[data-la-tint]` in `landing-art.css`) — never an
  // `applyTheme` swap, so the `<html>` pin, the visitor's stored
  // preference and the app's theme system stay untouched. Sage is the
  // entry state (no attribute) and the room returns to it at 02, and
  // for both CTA sections.
  const active = useActiveSection(PITCH_IDS, "top");
  const tint = PITCH_SECTIONS.find((section) => section.id === active)?.tint ?? null;

  return (
    <LandingMotion>
      <div
        data-la-root=""
        data-ps-root=""
        data-la-tint={tint ?? undefined}
        className="min-h-screen bg-bg"
      >
        <div className="la-pinned-header">
          <div className="la-pinned-header-inner">
            <MarketingHeader />
          </div>
        </div>
        <main className="relative z-10">
          <h1 className="sr-only">The problem with rostering</h1>
          <PitchOpening />
          <LandingTop />
          <LandingCosts />
          <LandingTail />
        </main>
        <ScrollRail sections={PITCH_SECTIONS} active={active} wordmark />
        {/* Reserve the page rail's gutter for the footer. */}
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 pb-16 sm:px-10 lg:pb-0 lg:pl-56">
          {/* Round 7: the shared columned footer — the round-5 member
            promises live in its first column; the full privacy model
            still lives at `/me/welcome` and on the marketing site. */}
          <MarketingFooter />
        </div>
      </div>
    </LandingMotion>
  );
}
